import React, { useEffect, useRef, useState } from "react";
import { RestaurantCard } from "./RestaurantCard";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontally scrollable row of restaurants — Netflix / UberEats style.
 * Native touch scroll on mobile, arrow buttons on desktop.
 */
export const RestaurantCarousel = ({
  title,
  subtitle,
  emoji,
  restaurants,
  testIdPrefix,
  accent = "#E63946",
}) => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Reset arrows immediately on data change, then re-measure once layout settles
    setCanScrollLeft(false);
    const raf = requestAnimationFrame(updateArrows);
    el.addEventListener("scroll", updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [restaurants]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85 * (dir === "left" ? -1 : 1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  if (!restaurants || restaurants.length === 0) return null;

  return (
    <section
      data-testid={`carousel-${testIdPrefix}`}
      className="relative"
    >
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-display font-black tracking-tight text-[#2B2B2B]">
            <span aria-hidden="true" className="mr-2">{emoji}</span>
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1 text-sm text-[#5C5C5C] font-mono">{subtitle}</p>
          )}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            data-testid={`carousel-${testIdPrefix}-prev`}
            aria-label="Précédent"
            onClick={() => scrollBy("left")}
            disabled={!canScrollLeft}
            className="w-10 h-10 rounded-full border-2 border-[#2B2B2B] bg-white text-[#2B2B2B] flex items-center justify-center hover:bg-[#2B2B2B] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#2B2B2B] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid={`carousel-${testIdPrefix}-next`}
            aria-label="Suivant"
            onClick={() => scrollBy("right")}
            disabled={!canScrollRight}
            className="w-10 h-10 rounded-full border-2 border-[#2B2B2B] bg-white text-[#2B2B2B] flex items-center justify-center hover:bg-[#2B2B2B] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#2B2B2B] transition-colors"
            style={{ boxShadow: canScrollRight ? `3px 3px 0 ${accent}` : "none" }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 -mx-6 md:-mx-12 px-6 md:px-12"
        style={{ scrollbarWidth: "none" }}
      >
        {restaurants.map((r, i) => (
          <div
            key={r.id}
            data-testid={`carousel-${testIdPrefix}-item-${r.id}`}
            className="snap-start shrink-0 w-[75%] sm:w-[45%] md:w-[32%] lg:w-[24%] max-w-[300px]"
          >
            <RestaurantCard restaurant={r} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
};
